import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  device,
  PrimaryButtonV2,
  YouTubeModal,
  isPromotionEligible,
} from 'utils';
import {
  AlhamdulillahCalligraphy,
  AllahummaCalligraphy,
  BismillahCalligraphy,
  EditNavPattern,
  EditStepDone,
  NavConnectorActive,
  NavConnectorInactive,
  NavStepDoing,
  NavStepDone,
  NavStepTodo,
  SidebarPattern,
  TreeImage,
} from 'images';

const OPTIONAL_CREATE_STEPS = [18, 19, 20];
const VIDEO_URL = 'https://www.youtube.com/embed/JyfjVfbUs9E';

const SideBarWrapper = styled.aside`
  position: relative;
  height: 100%;
  direction: rtl;

  * {
    direction: ltr;
  }

  @media ${device.maxLaptop} {
    max-height: 210px;
    overflow: hidden;
    direction: ltr;
  }
`;

const NavigationWrapper = styled.nav`
  padding: 64px;
  width: 560px;
  position: relative;
  height: 100%;

  @media ${device.maxLaptop} {
    width: 100%;
    padding: 32px;
  }
`;

const NavContentWrapper = styled.nav`
  position: relative;
  z-index: 1;

  @media ${device.maxLaptop} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const BackgroundWrapper = styled.div`
  background: #e6f4d7;
  height: 100%;
`;

const BackgroundImage = styled.div`
  display: none;
  position: absolute;
  left: -176px;
  top: -100px;
  width: 352px;
  height: 352px;
  background: url(${TreeImage}) no-repeat left center;
  background-size: contain;
  opacity: 0.5;
  z-index: 0;
  pointer-events: none;
  clip-path: inset(100px 0 42px 0);

  @media ${device.maxLaptop} {
    display: block;
  }
`;

const WelcomeScreen = styled.div`
  display: flex;
  flex-direction: column;
  color: #4a5724;

  @media ${device.maxLaptop} {
    justify-content: center;
    align-items: center;
  }
`;

const WelcomeTitle = styled.div`
  h3 {
    color: #2b4212;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 24px;

    @media ${device.maxLaptop} {
      font-size: 16px;
      margin-bottom: 16px;
      text-align: center;
    }
  }
`;

const WelcomeText = styled.p`
  font-size: 1.25rem;
  color: #535862;
  margin-bottom: 2rem;

  @media ${device.maxLaptop} {
    font-size: 14px;
    margin-bottom: 14px;
    text-align: center;
  }
`;

const Quote = styled.blockquote`
  color: #535862;
  font-size: 16px;

  @media ${device.maxLaptop} {
    display: none;
  }
`;

const QuoteAttribution = styled.p`
  font-size: 14px;
  margin-top: 8px;

  @media ${device.maxLaptop} {
    display: none;
  }
`;

const NavGap = styled.p`
  height: 32px;
`;

const TreeWrapper = styled.p`
  z-index: 1;
  position: relative;
  left: 64px;
  bottom: 448px;
  max-width: 432px;

  @media ${device.maxLaptop} {
    display: none;
  }
`;

const CalligraphyWrapper = styled.p`
  * {
    max-height: 40px;

    @media ${device.maxLaptop} {
      max-height: 30px;
    }
  }
`;

const PatternWrapper = styled.p`
  position: absolute;
  left: -12px;
  top: 0;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
  height: 100%;

  @media ${device.maxLaptop} {
    display: none;
  }
`;

const MobileNavigationWrapper = styled.p`
  padding: 16px;
  margin: 0;
  position: relative;
  height: 100%;
  width: 100%;
  max-height: 210px;
  overflow: hidden;
  direction: ltr;
`;

const IntroBlock = styled.div`
  margin-bottom: 3rem;
`;

const IntroTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ $edit }) => ($edit ? '#181d27' : '#2b4212')};
`;

const IntroText = styled.p`
  font-size: 20px;
  font-weight: 400;
  color: #535862;
`;

const HelpVideoButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${({ $welcome }) => ($welcome ? '#4a5724' : '#3f621a')};
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  font-weight: ${({ $welcome }) => ($welcome ? 400 : 400)};
`;

const StepList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: ${({ $mobile }) => ($mobile ? 'row' : 'column')};
  gap: ${({ $edit }) => ($edit ? '4px' : '10px')};
  white-space: ${({ $mobile }) => ($mobile ? 'nowrap' : 'normal')};
`;

const StepItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: ${({ $edit }) => ($edit ? 'center' : 'flex-start')};
  gap: 12px;
  position: relative;
  height: ${({ $edit, $mobile }) => ($edit && !$mobile ? '44px' : 'auto')};
`;

const CreateStepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
`;

const StepButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

const StepIconWrap = styled.div`
  width: ${({ $active }) => ($active ? '30px' : '24px')};
  height: ${({ $active }) => ($active ? '36px' : '27px')};
  border-radius: 50%;
  margin-left: ${({ $active }) => ($active ? '-3px' : 0)};
  margin-top: ${({ $active }) => ($active ? '-3px' : 0)};
  background: ${({ $todo }) => ($todo ? '#ffffff' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionalStepIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f5f5f5;
  border: 1.5px solid #a4a7ae;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 6px;
    border-left: 2px solid #717680;
    border-bottom: 2px solid #717680;
    top: 46%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const VerticalConnector = styled.div`
  position: absolute;
  left: 12px;
  top: 29px;
  width: 2px;
  height: 34px;
  transform: translateX(-50%);
`;

const HorizontalConnector = styled.div`
  position: absolute;
  left: 41px;
  top: 20px;
  width: 2px;
  height: 48px;
  transform: translateY(-50%);

  img {
    transform: rotate(90deg);
  }
`;

const StepText = styled.div`
  flex: 1;
`;

const StepTitle = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  font-weight: 600;
  font-size: 14px;
  color: ${({ $active }) => ($active ? '#3f621a' : '#414651')};
  margin-bottom: 0.25rem;
  cursor: pointer;
  text-align: left;
`;

const StepDescription = styled.div`
  font-size: 14px;
  color: ${({ $active }) => ($active ? '#3f621a' : '#535862')};
  margin-bottom: 0.5rem;
`;

const EditStepRow = styled.button`
  border: 0;
  background: transparent;
  padding: 0 0 0 12px;
  margin-left: ${({ $active }) => ($active ? 0 : '2px')};
  border-left: ${({ $active }) => ($active ? '2px solid #9dbc60' : '0')};
  height: 44px;
  color: ${({ $active }) => ($active ? '#3f621a' : '#717680')};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  text-align: left;
`;

const MobileScroller = styled.div`
  display: flex;
  flex: 1;
  justify-content: ${({ $edit }) => ($edit ? 'flex-start' : 'center')};
  overflow-x: ${({ $edit }) => ($edit ? 'auto' : 'visible')};
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${({ $edit }) => ($edit ? '16px' : 0)};
`;

const PreviewButton = styled(PrimaryButtonV2)`
  margin-top: 24px;
  color: #414651;
`;

function readUserFirstName() {
  try {
    return JSON.parse(localStorage.getItem('user'))?.first_name;
  } catch {
    return null;
  }
}

function readSessionProjectType() {
  try {
    const value = sessionStorage.getItem('categoryType');
    return value ? JSON.parse(value)?.project_type : null;
  } catch {
    return null;
  }
}

function getIsEvent(project, projectId) {
  if (project && projectId) {
    return project?.fundingTarget?.is_ticketable ?? false;
  }

  return readSessionProjectType() === 'event';
}

function isStripeComplete(status) {
  return status === 'enabled_and_eventually_due' || status === 'enabled';
}

function createSingleSubStep(id, title, currentView, isCompleted) {
  return {
    id,
    title,
    isActive: currentView === id,
    isCompleted,
  };
}

function createStep({
  id,
  viewId,
  title,
  description,
  currentView,
  isCompleted,
  isOptional = false,
  hasSelection = false,
  subSteps,
}) {
  return {
    id,
    title,
    description,
    isActive: subSteps
      ? subSteps.some((step) => step.isActive)
      : currentView === viewId,
    isCompleted,
    isOptional,
    hasSelection,
    subSteps:
      subSteps || [createSingleSubStep(viewId, title, currentView, isCompleted)],
  };
}

function buildCreateSteps({
  currentView,
  projectId,
  project,
  isEvent,
  promotionsModuleEnabled,
  minFundingGoal,
}) {
  const isProjectCreated = Boolean(projectId);
  const {
    basicInfo,
    projectDescription,
    media,
    contractDocuments,
    isGoodiesDone,
    isPromotionsDone,
    isPersonalizationDone,
    is_support_levels_done: isSupportLevelsDone,
    support_levels: supportLevels,
    rewards,
    selected_promotion_ids: selectedPromotionIds,
  } = project || {};

  const steps = [
    createStep({
      id: 1,
      viewId: 4,
      title: isEvent ? 'Dein Event' : 'Dein Projekt',
      description: isEvent
        ? 'Erzähle uns mehr über dein Event.'
        : 'Erzähle uns mehr über dein Projekt.',
      currentView,
      isCompleted: Boolean(basicInfo?.isDone && isProjectCreated),
      subSteps: [
        createSingleSubStep(
          4,
          'Grundlegendes',
          currentView,
          Boolean(basicInfo?.isDone && isProjectCreated)
        ),
      ],
    }),
    createStep({
      id: 2,
      viewId: 5,
      title: 'Zusätzliche Angaben',
      description: 'Beantworte häufig gestellte Fragen.',
      currentView,
      isCompleted: Boolean(projectDescription?.isDone && isProjectCreated),
      subSteps: [
        createSingleSubStep(
          5,
          'Projektinformationen',
          currentView,
          Boolean(projectDescription?.isDone && isProjectCreated)
        ),
      ],
    }),
    createStep({
      id: 3,
      viewId: 6,
      title: 'Medien',
      description: 'Lade Bilder oder ein Video hoch.',
      currentView,
      isCompleted: Boolean(media?.isDone && isProjectCreated),
      subSteps: [
        createSingleSubStep(
          6,
          'Bilder und Videos',
          currentView,
          Boolean(media?.isDone && isProjectCreated)
        ),
      ],
    }),
  ];

  if (!isEvent) {
    const supportLevelsDone = Boolean(isSupportLevelsDone && isProjectCreated);

    steps.push(
      createStep({
        id: 18,
        viewId: 18,
        title: 'Unterstützungsstufen',
        description: 'Definiere Anhaltspunkte für deine Unterstützer.',
        currentView,
        isCompleted: supportLevelsDone,
        isOptional: true,
        hasSelection: Boolean(supportLevels?.length),
      })
    );
  }

  const goodiesDone = Boolean(isGoodiesDone && isProjectCreated);

  steps.push(
    createStep({
      id: 4,
      viewId: 7,
      title: isEvent ? 'Tickets' : 'Goodies',
      description: isEvent
        ? 'Stelle deine Tickets ein.'
        : 'Biete deiner Community eine Gegenleistung.',
      currentView,
      isCompleted: goodiesDone,
      isOptional: !isEvent,
      hasSelection: isEvent || Boolean(rewards?.length),
      subSteps: [
        createSingleSubStep(
          7,
          isEvent ? 'Tickets' : 'Goodies',
          currentView,
          goodiesDone
        ),
      ],
    })
  );

  if (
    promotionsModuleEnabled &&
    !isEvent &&
    isPromotionEligible(project, minFundingGoal)
  ) {
    const promotionsDone = Boolean(isPromotionsDone && isProjectCreated);

    steps.push(
      createStep({
        id: 20,
        viewId: 20,
        title: 'Promotions (optional)',
        description: 'Verstärke den Community-Effekt.',
        currentView,
        isCompleted: promotionsDone,
        isOptional: true,
        hasSelection: Boolean(selectedPromotionIds?.length),
        subSteps: [
          createSingleSubStep(20, 'Promotions', currentView, promotionsDone),
        ],
      })
    );
  }

  const personalizationDone = Boolean(isPersonalizationDone && isProjectCreated);

  steps.push(
    createStep({
      id: 19,
      viewId: 19,
      title: 'Persönliche Angaben',
      description: 'Bestätige deine persönlichen Angaben',
      currentView,
      isCompleted: personalizationDone,
    }),
    createStep({
      id: 5,
      title: 'Einreichung & Zahlungsabwicklung',
      description: 'Der letzte Schritt, bevor es losgeht!',
      currentView,
      isCompleted: Boolean(contractDocuments?.isDone && isProjectCreated),
      subSteps: [
        createSingleSubStep(8, 'Dokumente', currentView, false),
        createSingleSubStep(
          9,
          'Einreichung',
          currentView,
          Boolean(contractDocuments?.isDone && isProjectCreated)
        ),
      ],
    })
  );

  return steps;
}

function buildEditSteps({
  currentView,
  projectId,
  project,
  isEvent,
  promotionsModuleEnabled,
  minFundingGoal,
}) {
  const isProjectCreated = Boolean(projectId);
  const {
    basicInfo,
    projectDescription,
    media,
    contractDocuments,
    fundingTarget,
    stripeStatus,
    isGoodiesDone,
    isPromotionsDone,
    isPersonalizationDone,
    is_support_levels_done: isSupportLevelsDone,
    support_levels: supportLevels,
    rewards,
    selected_promotion_ids: selectedPromotionIds,
  } = project || {};

  const steps = [
    createStep({
      id: 1,
      viewId: 1,
      title: 'Grundlegendes',
      currentView,
      isCompleted: true,
    }),
    createStep({
      id: 2,
      viewId: 2,
      title: 'Projektbeschreibung',
      currentView,
      isCompleted: Boolean(basicInfo?.isDone && isProjectCreated),
    }),
    createStep({
      id: 3,
      viewId: 3,
      title: 'Zusätzliche Angaben',
      currentView,
      isCompleted: Boolean(projectDescription?.isDone && isProjectCreated),
    }),
  ];

  if (!isEvent) {
    steps.push(
      createStep({
        id: 4,
        viewId: 4,
        title: 'Fundingziel',
        currentView,
        isCompleted: Boolean(fundingTarget?.isDone && isProjectCreated),
      })
    );
  }

  if (!isEvent) {
    steps.push(
      createStep({
        id: 18,
        viewId: 18,
        title: 'Unterstützungsstufen',
        description: 'Definiere Anhaltspunkte für deine Unterstützer.',
        currentView,
        isCompleted: Boolean(isSupportLevelsDone && isProjectCreated),
        isOptional: true,
        hasSelection: Boolean(supportLevels?.length),
      })
    );
  }

  steps.push(
    createStep({
      id: 19,
      viewId: 19,
      title: 'Persönliche Angaben',
      description: 'Bestätige deine persönlichen Angaben',
      currentView,
      isCompleted: Boolean(isPersonalizationDone && isProjectCreated),
    })
  );

  if (
    promotionsModuleEnabled &&
    !isEvent &&
    isPromotionEligible(project, minFundingGoal)
  ) {
    steps.push(
      createStep({
        id: 20,
        viewId: 20,
        title: 'Promotions (optional)',
        description: 'Verstärke den Community-Effekt.',
        currentView,
        isCompleted: Boolean(isPromotionsDone && isProjectCreated),
        isOptional: true,
        hasSelection: Boolean(selectedPromotionIds?.length),
        subSteps: [
          createSingleSubStep(
            20,
            'Promotions',
            currentView,
            Boolean(isPromotionsDone && isProjectCreated)
          ),
        ],
      })
    );
  }

  steps.push(
    createStep({
      id: 5,
      viewId: 5,
      title: 'Medien',
      currentView,
      isCompleted: Boolean(media?.isDone && isProjectCreated),
    }),
    createStep({
      id: 6,
      viewId: 6,
      title: isEvent ? "Tickets" : 'Goodies',
      currentView,
      isCompleted: Boolean(isGoodiesDone && isProjectCreated),
      isOptional: !isEvent,
      hasSelection: isEvent || Boolean(rewards?.length),
    }),
    createStep({
      id: 7,
      viewId: 7,
      title: 'Zahlungsabwicklung',
      currentView,
      isCompleted: Boolean(isStripeComplete(stripeStatus) && isProjectCreated),
    }),
    createStep({
      id: 8,
      viewId: 8,
      title: 'Einreichung',
      currentView,
      isCompleted: Boolean(contractDocuments?.isDone && isProjectCreated),
    })
  );

  return steps;
}

class SideBar extends Component {
  static propTypes = {
    handelCurrentView: PropTypes.func.isRequired,
    currentView: PropTypes.number.isRequired,
    projectId: PropTypes.string,
    project: PropTypes.object.isRequired,
    isStripeDone: PropTypes.bool.isRequired,
    canMove: PropTypes.bool.isRequired,
    handelShowModal: PropTypes.func.isRequired,
    setSelectedStep: PropTypes.func.isRequired,
    getPayPalStatus: PropTypes.bool.isRequired,
    mobileFormShow: PropTypes.bool.isRequired,
    editProject: PropTypes.bool.isRequired,
    promotionsModuleEnabled: PropTypes.bool,
    minFundingGoal: PropTypes.number,
  };

  state = {
    isEvent: false,
    showVideoModal: false,
  };

  componentDidMount() {
    this.props.setSelectedStep(7);
    this.syncEventType();
    this.syncExternalRedirectState();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.project !== this.props.project ||
      prevProps.projectId !== this.props.projectId
    ) {
      this.syncEventType();
    }
  }

  getCreateSteps = () =>
    buildCreateSteps({
      currentView: this.props.currentView,
      projectId: this.props.projectId,
      project: this.props.project,
      isEvent: this.state.isEvent,
      promotionsModuleEnabled: this.props.promotionsModuleEnabled,
      minFundingGoal: this.props.minFundingGoal,
    });

  getEditSteps = () =>
    buildEditSteps({
      currentView: this.props.currentView,
      projectId: this.props.projectId,
      project: this.props.project,
      isEvent: this.state.isEvent,
      promotionsModuleEnabled: this.props.promotionsModuleEnabled,
      minFundingGoal: this.props.minFundingGoal,
    });

  syncExternalRedirectState = () => {
    const { isStripeDone, setSelectedStep, handelCurrentView } = this.props;

    if (window.location.href.includes('merchantId')) {
      setSelectedStep(8);
      handelCurrentView(8);
      return;
    }

    if (isStripeDone) {
      setSelectedStep(8);
    }
  };

  syncEventType = () => {
    const isEvent = getIsEvent(this.props.project, this.props.projectId);

    if (this.state.isEvent !== isEvent) {
      this.setState({ isEvent });
    }
  };

  isWelcomePage = () => [1, 2, 3].includes(this.props.currentView);

  getSidebarTitle = () => {
    const { currentView } = this.props;
    const username = readUserFirstName() || 'User';
    const projectType = this.state.isEvent ? 'Event' : 'Projekt';

    switch (currentView) {
      case 1:
        return {
          calligraphy: BismillahCalligraphy,
          alt: 'Bismillah calligraphy',
          title: (
            <>
              Bismillahi ar-Rahmani ar-Rahim.
              <br />
              Lass uns loslegen.
            </>
          ),
        };
      case 2:
        return {
          calligraphy: AlhamdulillahCalligraphy,
          alt: 'Alhamdulillah calligraphy',
          title: "Alhamdulillah rabbil 'alamin.",
        };
      case 3:
        return {
          calligraphy: AllahummaCalligraphy,
          alt: 'Allahumma calligraphy',
          title: "Allahumma salli 'ala Muhammad wa 'ala ali Muhammad.",
        };
      case 6:
        return { title: 'Mach dein Projekt sichtbar.' };
      case 7:
        return {
          title: (
            <>
              Biete deiner Community eine
              <br />
              Gegenleistung.
            </>
          ),
        };
      case 20:
        return {
          title: (
            <>
              Verstärke den
              <br />
              Community-Effekt.
            </>
          ),
        };
      case 8:
      case 9:
        return { title: 'Wir sind gleich soweit.' };
      default:
        return {
          title: (
            <>
              Salam, {username}.
              <br />
              Erzähle uns mehr über dein {projectType}.
            </>
          ),
        };
    }
  };

  handleCreateStepClick = (stepId) => {
    const { handelCurrentView, projectId, setSelectedStep } = this.props;
    const steps = this.getCreateSteps();
    const clickedIndex = steps.findIndex((step) =>
      step.subSteps?.some((subStep) => subStep.id === stepId)
    );

    if (clickedIndex === -1) return;

    const previousStepsCompleted = steps
      .slice(0, clickedIndex)
      .every((step) => step.isCompleted);
    const canOpenStep =
      clickedIndex === 0 ||
      previousStepsCompleted ||
      OPTIONAL_CREATE_STEPS.includes(stepId);

    if (!canOpenStep) return;

    setSelectedStep(projectId && stepId);
    handelCurrentView(stepId);
  };

  handleEditStepClick = (stepId) => {
    const { handelCurrentView, projectId, setSelectedStep } = this.props;

    setSelectedStep(projectId && stepId);
    handelCurrentView(stepId);
  };

  handleShowVideo = () => this.setState({ showVideoModal: true });

  handleHideVideo = () => this.setState({ showVideoModal: false });

  renderSidebarTitle = () => {
    const { mobileFormShow } = this.props;
    const { calligraphy, alt, title } = this.getSidebarTitle();
    const shouldShowCalligraphy = calligraphy || mobileFormShow;

    return (
      <>
        {shouldShowCalligraphy && (
          <CalligraphyWrapper>
            <img
              src={calligraphy || BismillahCalligraphy}
              alt={alt || 'Bismillah calligraphy'}
            />
          </CalligraphyWrapper>
        )}
        <WelcomeTitle>
          <h3>{title}</h3>
        </WelcomeTitle>
      </>
    );
  };

  renderVideoHelp = (welcome = false) => (
    <>
      Falls Du Hilfe brauchst oder Fragen hast,
      <br />
      schau dir gerne{' '}
      <HelpVideoButton
        type="button"
        $welcome={welcome}
        onClick={this.handleShowVideo}
      >
        unser Erklärvideo
      </HelpVideoButton>{' '}
      an.
    </>
  );

  renderWelcomeScreen = () => (
    <WelcomeScreen>
      <NavContentWrapper>
        {this.renderSidebarTitle()}
        <WelcomeText>{this.renderVideoHelp(true)}</WelcomeText>
        <Quote>
          &quot;Gewiss, die (Gültigkeit der) Taten hängt von den Absichten ab,
          und jeder Mensch bekommt das, was er beabsichtigt hat.&quot;
          <QuoteAttribution>— sinngemäß Sayyiduna Muhammad ﷺ</QuoteAttribution>
        </Quote>
      </NavContentWrapper>

      <PatternWrapper>
        <div>
          <img src={SidebarPattern} alt="Pattern" />
        </div>
      </PatternWrapper>
      <NavGap />
    </WelcomeScreen>
  );

  renderStepIcon = (step) => {
    if (step.isActive) {
      return (
        <StepIconWrap $active>
          <img src={NavStepDoing} alt="Navigation step doing" />
        </StepIconWrap>
      );
    }

    if (step.isOptional && step.isCompleted && !step.hasSelection) {
      return <OptionalStepIcon aria-label="Option nicht ausgewählt" />;
    }

    if (step.isCompleted) {
      return (
        <StepIconWrap>
          <img src={NavStepDone} alt="Navigation step done" />
        </StepIconWrap>
      );
    }

    return (
      <StepIconWrap $todo>
        <img src={NavStepTodo} alt="Navigation step todo" />
      </StepIconWrap>
    );
  };

  renderConnector = ({ active, mobile }) => {
    const Connector = mobile ? HorizontalConnector : VerticalConnector;

    return (
      <Connector>
        <img
          src={active ? NavConnectorActive : NavConnectorInactive}
          alt={
            active
              ? 'Navigation connection active'
              : 'Navigation connection inactive'
          }
        />
      </Connector>
    );
  };

  renderCreateDesktopStep = (step, index, steps) => {
    const nextStep = steps[index + 1];
    const hasNextStep = index < steps.length - 1;
    const firstSubStepId = step.subSteps[0].id;

    return (
      <CreateStepItem key={`${step.id}-${index}`}>
        {hasNextStep &&
          this.renderConnector({
            active: nextStep?.isCompleted,
            mobile: false,
          })}

        <StepButton
          type="button"
          onClick={() => this.handleCreateStepClick(firstSubStepId)}
        >
          {this.renderStepIcon(step)}
        </StepButton>

        <StepText>
          <StepTitle
            type="button"
            $active={step.isActive}
            onClick={() => this.handleCreateStepClick(firstSubStepId)}
          >
            {step.title}
          </StepTitle>
          <StepDescription $active={step.isActive}>
            {step.description}
          </StepDescription>
        </StepText>
      </CreateStepItem>
    );
  };

  renderMobileStep = (step, index, steps, onClick) => {
    const nextStep = steps[index + 1];
    const hasNextStep = index < steps.length - 1;
    const firstSubStepId = step.subSteps[0].id;

    return (
      <StepItem key={`${step.id}-${index}`} $mobile>
        {hasNextStep &&
          this.renderConnector({
            active: nextStep?.isActive || nextStep?.isCompleted,
            mobile: true,
          })}

        <StepButton type="button" onClick={() => onClick(firstSubStepId)}>
          {this.renderStepIcon(step)}
        </StepButton>
      </StepItem>
    );
  };

  renderCreateNavigation = () => {
    const { mobileFormShow } = this.props;
    const steps = this.getCreateSteps();

    return (
      <>
        <NavContentWrapper>
          {mobileFormShow ? (
            this.renderWelcomeScreen()
          ) : (
            <IntroBlock>
              <IntroTitle>{this.renderSidebarTitle()}</IntroTitle>
              <IntroText>{this.renderVideoHelp()}</IntroText>
            </IntroBlock>
          )}

          {!mobileFormShow && (
            <div style={{ flex: 1 }}>
              <StepList>
                {steps.map((step, index) =>
                  this.renderCreateDesktopStep(step, index, steps)
                )}
              </StepList>
            </div>
          )}
        </NavContentWrapper>

        <NavGap />
        <PatternWrapper>
          <div style={{ marginTop: '184px' }}>
            <img src={SidebarPattern} alt="Pattern" />
          </div>
        </PatternWrapper>
      </>
    );
  };

  renderCreateMobileNavigation = () => {
    const steps = this.getCreateSteps();

    return (
      <MobileScroller>
        <StepList $mobile>
          {steps.map((step, index) =>
            this.renderMobileStep(step, index, steps, this.handleCreateStepClick)
          )}
        </StepList>
      </MobileScroller>
    );
  };

  renderEditDesktopStep = (step) => {
    const firstSubStepId = step.subSteps[0].id;

    return (
      <StepItem key={step.id} $edit>
        <EditStepRow
          type="button"
          $active={step.isActive}
          onClick={() => this.handleEditStepClick(firstSubStepId)}
        >
          {step.title}
        </EditStepRow>

        {step.isOptional && step.isCompleted && !step.hasSelection ? (
          <OptionalStepIcon aria-label="Option nicht ausgewählt" />
        ) : step.isCompleted ? (
          <StepIconWrap>
            <img src={EditStepDone} alt="Navigation step done" />
          </StepIconWrap>
        ) : null}
      </StepItem>
    );
  };

  renderEditNavigation = () => {
    const { mobileFormShow, projectId } = this.props;
    const steps = this.getEditSteps();

    return (
      <>
        <NavContentWrapper>
          {mobileFormShow ? (
            this.renderWelcomeScreen()
          ) : (
            <IntroBlock>
              <IntroTitle $edit>Projekt bearbeiten</IntroTitle>
            </IntroBlock>
          )}

          {!mobileFormShow && (
            <div style={{ flex: 1 }}>
              <StepList $edit>{steps.map(this.renderEditDesktopStep)}</StepList>
              <PreviewButton primary={true} minWidth="160px">
                <Link to={`/view/${projectId}`}>Projektvorschau</Link>
              </PreviewButton>
            </div>
          )}
        </NavContentWrapper>

        <PatternWrapper>
          <div>
            <img src={EditNavPattern} alt="Pattern" />
          </div>
        </PatternWrapper>
      </>
    );
  };

  renderEditMobileNavigation = () => {
    const steps = this.getEditSteps();

    return (
      <MobileScroller $edit>
        <StepList $mobile>
          {steps.map((step, index) =>
            this.renderMobileStep(step, index, steps, this.handleEditStepClick)
          )}
        </StepList>
      </MobileScroller>
    );
  };

  renderSidebarContent = () => {
    if (this.props.editProject) return this.renderEditNavigation();
    if (this.isWelcomePage()) return this.renderWelcomeScreen();
    return this.renderCreateNavigation();
  };

  renderMobileNavigation = () => {
    const { editProject, mobileFormShow } = this.props;

    if (!mobileFormShow) return null;
    if (editProject) return this.renderEditMobileNavigation();
    if (this.isWelcomePage()) return null;

    return (
      <MobileNavigationWrapper>
        {this.renderCreateMobileNavigation()}
      </MobileNavigationWrapper>
    );
  };

  render() {
    const { editProject } = this.props;
    const { showVideoModal } = this.state;

    return (
      <>
        <BackgroundWrapper>
          <BackgroundImage />
          <SideBarWrapper>
            <NavigationWrapper>{this.renderSidebarContent()}</NavigationWrapper>
            <YouTubeModal
              link={VIDEO_URL}
              showModal={showVideoModal}
              closeModal={this.handleHideVideo}
            />
          </SideBarWrapper>

          {!editProject && (
            <TreeWrapper>
              <div
                style={{
                  marginTop: 'auto',
                  textAlign: 'center',
                  padding: 0,
                }}
              >
                <img src={TreeImage} alt="Tree illustration" />
              </div>
            </TreeWrapper>
          )}
        </BackgroundWrapper>

        {this.renderMobileNavigation()}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { project, loading, errors } = state.projects;
  return {
    project,
    loading,
    errors,
  };
};

export default connect(mapStateToProps)(SideBar);
